import { Link, useLocation, Outlet, useNavigate, Navigate } from "react-router-dom";
import {
  Building2, LayoutDashboard, Users, CreditCard, LogOut, ShieldCheck,
  Receipt, Wallet, FileText, UserCog, ChevronRight, Mail,
} from "lucide-react";
import { clearSession, getToken, getUser } from "../../lib/auth";

const SECTIONS = [
  {
    group: "Overview",
    items: [{ lbl: "Dashboard", to: "/admin/dashboard", icon: LayoutDashboard }],
  },
  {
    group: "Tenant Management",
    items: [
      { lbl: "Tenants", to: "/admin/tenants", icon: Building2 },
      { lbl: "Super Admins", to: "/admin/super-admins", icon: UserCog },
    ],
  },
  {
    group: "Billing",
    items: [
      { lbl: "Plans", to: "/admin/plans", icon: CreditCard },
      { lbl: "Subscriptions", to: "/admin/subscriptions", icon: Users },
      { lbl: "Transactions", to: "/admin/transactions", icon: Receipt },
      { lbl: "Pending Payments", to: "/admin/pending-payments", icon: Wallet },
      { lbl: "Invoices", to: "/admin/invoices", icon: FileText },
      { lbl: "Payment Gateway", to: "/admin/payment-gateway", icon: ShieldCheck },
    ],
  },
  {
    group: "Content",
    items: [
      { lbl: "Blog", to: "/admin/blog", icon: FileText },
    ],
  },
  {
    group: "System",
    items: [
      { lbl: "Email Settings", to: "/admin/email-settings", icon: Mail },
    ],
  },
];

export default function AdminLayout() {
  const loc = useLocation();
  const nav = useNavigate();
  const user = getUser();
  const token = getToken();

  if (!token || !user?.is_super_admin) return <Navigate to="/admin/login" replace />;

  const logout = () => { clearSession(); nav("/admin/login"); };

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-[#FFFCF2] flex">
      <aside className="w-64 bg-white border-r border-black/8 flex flex-col sticky top-0 h-screen">
        <div className="p-5 border-b border-black/8">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-[#1B4332] flex items-center justify-center">
              <ShieldCheck size={20} className="text-white" />
            </div>
            <div>
              <div className="font-display font-extrabold text-lg text-black leading-tight">Brivo</div>
              <div className="text-[11px] text-black/50 -mt-0.5 uppercase tracking-wider">Master Admin</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-5 overflow-y-auto">
          {SECTIONS.map((sec) => (
            <div key={sec.group}>
              <div className="px-3 pb-1.5 text-[10px] uppercase tracking-[0.12em] text-black/40 font-bold">
                {sec.group}
              </div>
              <div className="space-y-0.5">
                {sec.items.map((it) => {
                  const active = loc.pathname === it.to || loc.pathname.startsWith(it.to + "/");
                  const Icon = it.icon;
                  return (
                    <Link
                      key={it.to}
                      to={it.to}
                      data-testid={`admin-nav-${it.lbl.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        active
                          ? "bg-[#1B4332] text-white"
                          : "text-black/70 hover:bg-[#F4F8F3] hover:text-black"
                      }`}
                    >
                      <Icon size={16} className={active ? "text-white" : "text-black/50 group-hover:text-[#1B4332]"} />
                      <span className="flex-1">{it.lbl}</span>
                      {active && <ChevronRight size={14} className="text-white/70" />}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-black/8">
          <div className="flex items-center gap-2.5 px-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-[#E8F5E9] text-[#1B4332] text-xs font-bold flex items-center justify-center">
              {(user?.name || "A").slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-black truncate">{user?.name}</div>
              <div className="text-[11px] text-black/50 truncate">{user?.email}</div>
            </div>
          </div>
          <button onClick={logout} data-testid="admin-logout"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-black/70 hover:bg-red-50 hover:text-red-600 transition-colors">
            <LogOut size={14} /> Log out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
